import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Lock, LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [key, setKey] = useState(sessionStorage.getItem("bms_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [tab, setTab] = useState("referrals");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Key": k };
      const [refRes, conRes] = await Promise.all([
        axios.get(`${API}/referrals`, { headers }),
        axios.get(`${API}/consultations`, { headers }),
      ]);
      setReferrals(refRes.data);
      setConsultations(conRes.data);
      setAuthed(true);
      sessionStorage.setItem("bms_admin_key", k);
    } catch {
      setError("Invalid admin key. Please try again.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (e) => {
    e.preventDefault();
    load(key);
  };

  const logout = () => {
    sessionStorage.removeItem("bms_admin_key");
    setKey("");
    setAuthed(false);
    setReferrals([]);
    setConsultations([]);
  };

  return (
    <div data-testid="admin-page" className="bg-brand-paper min-h-screen">
      <Header />
      <main className="pt-[84px]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
          {!authed ? (
            <div className="max-w-md mx-auto rounded-2xl border border-slate-200 bg-white p-10 shadow-[0_8px_30px_rgb(11,27,61,0.06)]">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                <Lock className="h-6 w-6" strokeWidth={1.6} />
              </span>
              <h1 className="mt-5 font-display text-3xl text-brand-navy">Admin Dashboard</h1>
              <p className="mt-2 text-sm text-slate-600">Enter the admin key to view submitted patient referrals and consultation requests.</p>
              <form onSubmit={handleLogin} className="mt-6 space-y-4">
                <input
                  type="password"
                  data-testid="admin-key-input"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="Admin key"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
                />
                {error && <p data-testid="admin-login-error" className="text-xs font-semibold text-red-700" role="alert">{error}</p>}
                <button
                  type="submit"
                  data-testid="admin-login-button"
                  disabled={loading}
                  className="w-full rounded-full bg-brand-teal px-6 py-3.5 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60"
                >
                  {loading ? "Verifying…" : "View Dashboard"}
                </button>
              </form>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Admin Dashboard</h1>
                <div className="flex gap-3">
                  <button
                    onClick={() => load(key)}
                    data-testid="admin-refresh-button"
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-brand-navy hover:bg-white transition-colors duration-300"
                  >
                    <RefreshCw className="h-4 w-4" strokeWidth={1.8} /> Refresh
                  </button>
                  <button
                    onClick={logout}
                    data-testid="admin-logout-button"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-teal transition-colors duration-300"
                  >
                    <LogOut className="h-4 w-4" strokeWidth={1.8} /> Log Out
                  </button>
                </div>
              </div>

              <div className="mt-8 flex gap-2" role="tablist">
                <button
                  role="tab"
                  aria-selected={tab === "referrals"}
                  onClick={() => setTab("referrals")}
                  data-testid="admin-tab-referrals"
                  className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${tab === "referrals" ? "bg-brand-navy text-white" : "bg-white border border-slate-300 text-brand-navy hover:bg-brand-mist"}`}
                >
                  Patient Referrals ({referrals.length})
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "consultations"}
                  onClick={() => setTab("consultations")}
                  data-testid="admin-tab-consultations"
                  className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${tab === "consultations" ? "bg-brand-navy text-white" : "bg-white border border-slate-300 text-brand-navy hover:bg-brand-mist"}`}
                >
                  Consultation Requests ({consultations.length})
                </button>
              </div>

              {tab === "referrals" && (
                referrals.length === 0 ? (
                  <div data-testid="referrals-empty" className="mt-8 rounded-2xl border border-slate-200 bg-white p-14 text-center text-slate-500">
                    No referrals submitted yet.
                  </div>
                ) : (
                  <div className="mt-8 space-y-5">
                    {referrals.map((r) => (
                      <article key={r.id} data-testid={`referral-item-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_8px_30px_rgb(11,27,61,0.04)]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h2 className="font-display text-2xl text-brand-navy">{r.patient_name}</h2>
                            <p className="text-sm text-slate-500 mt-1">DOB: {r.patient_dob} · Submitted: {new Date(r.submitted_at).toLocaleString()}</p>
                          </div>
                          <span className="rounded-full bg-brand-teal/10 px-4 py-1.5 text-xs font-bold text-brand-teal">New Referral</span>
                        </div>
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contact</p>
                            <p className="mt-2 text-slate-700">{r.patient_phone}<br />{r.patient_email}<br />{r.patient_address}</p>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Requested Support</p>
                            <ul className="mt-2 text-slate-700 list-disc list-inside">
                              {r.requested_support.map((s) => <li key={s}>{s}</li>)}
                              {r.other_support && <li>Other: {r.other_support}</li>}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Preferred Start</p>
                            <p className="mt-2 text-slate-700">{r.preferred_start_date || "Not specified"}<br />{r.preferred_days_hours}</p>
                          </div>
                        </div>
                        <div className="mt-5 text-sm">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Reason for Referral</p>
                          <p className="mt-2 text-slate-700 leading-relaxed">{r.reason}</p>
                        </div>
                        {r.care_considerations && (
                          <div className="mt-4 text-sm">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Care Considerations</p>
                            <p className="mt-2 text-slate-700 leading-relaxed">{r.care_considerations}</p>
                          </div>
                        )}
                        <p className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500">
                          Signed by {r.signature_name} on {r.signature_date}
                          {r.emergency_name && <> · Emergency contact: {r.emergency_name} ({r.emergency_relationship}) {r.emergency_phone}</>}
                          {r.referrer_name && <> · Referred by: {r.referrer_name}{r.referrer_clinic && `, ${r.referrer_clinic}`}</>}
                        </p>
                      </article>
                    ))}
                  </div>
                )
              )}

              {tab === "consultations" && (
                consultations.length === 0 ? (
                  <div data-testid="consultations-empty" className="mt-8 rounded-2xl border border-slate-200 bg-white p-14 text-center text-slate-500">
                    No consultation requests yet.
                  </div>
                ) : (
                  <div className="mt-8 space-y-5">
                    {consultations.map((c) => (
                      <article key={c.id} data-testid={`consultation-item-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_8px_30px_rgb(11,27,61,0.04)]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h2 className="font-display text-2xl text-brand-navy">{c.name}</h2>
                            <p className="text-sm text-slate-500 mt-1">Submitted: {new Date(c.submitted_at).toLocaleString()}</p>
                          </div>
                          <span className="rounded-full bg-brand-teal/10 px-4 py-1.5 text-xs font-bold text-brand-teal">Consultation Request</span>
                        </div>
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contact</p>
                            <p className="mt-2 text-slate-700">{c.phone}<br />{c.email}</p>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Preferred Date & Time</p>
                            <p className="mt-2 text-slate-700">{c.preferred_date}<br />{c.preferred_time}</p>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Care Needed For</p>
                            <p className="mt-2 text-slate-700">{c.care_for || "Not specified"}</p>
                          </div>
                        </div>
                        {c.message && (
                          <div className="mt-5 text-sm">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Message</p>
                            <p className="mt-2 text-slate-700 leading-relaxed">{c.message}</p>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
